import { FC } from 'react';
import { ENV_CONFIG } from 'config/environment';
import { Image } from 'components/Image';
import Icon, { IconNames } from 'components/Icon';
import Loader from 'components/Loader';
import { IRecipient } from 'store/recipients/types';

interface Props {
  recipients: IRecipient[];
  onRemove: (index: number) => void;
  primaryColor: string;
  secondaryColor: string;
  hideHeader?: boolean;
  /** 'top' = mobile: button at top; 'after-list' = button after all recipients; number = button after first N recipients */
  donatePosition?: 'top' | 'after-list' | number;
  onDonate?: () => void;
  isLoading?: boolean;
}

const getSubtitle = (recipient: IRecipient) => {
  return recipient.race?.election.display_info || recipient.race?.election.state || '';
};

const renderItem = (recipient: IRecipient, index: number, onRemove: (index: number) => void) => (
  <div className="sidebar-item-row" key={recipient.recipient_id || index}>
    <div className="sidebar-item-left">
      <Image
        alt={`Recipient ${recipient.recipient_name}`}
        src={`${ENV_CONFIG().CLOUD_STORAGE.URL}/recipients/rec_${recipient.misc_ids?.photo_id}.jpg`}
        fallbackImgType="candidate_square"
        className="candidate-photo"
      />
      <div className="sidebar-item-text">
        <span className="body-small-text item-name">{recipient.recipient_name}</span>
        <span className="utility-text color-black shadow-5">{getSubtitle(recipient)}</span>
      </div>
    </div>
    <div
      className="remove-button"
      aria-label={`Remove ${recipient.recipient_name} from cart`}
      onClick={() => onRemove(index)}
    >
      <Icon name={IconNames.XClose} size={18} />
    </div>
  </div>
);

const DonateButton = ({
  primaryColor,
  secondaryColor,
  onClick,
  wrapperClassName,
}: {
  primaryColor: string;
  secondaryColor: string;
  onClick: () => void;
  wrapperClassName: string;
}) => (
  <div className={wrapperClassName}>
    <button
      className="button button-brand full-width sticky-button"
      style={{ backgroundColor: primaryColor, color: secondaryColor }}
      onClick={onClick}
    >
      DONATE NOW
    </button>
  </div>
);

export const SelectedRecipientSidebarCard: FC<Props> = ({
  recipients: recipients,
  onRemove,
  primaryColor,
  secondaryColor,
  hideHeader,
  donatePosition = 'after-list',
  onDonate,
  isLoading = false,
}) => {
  const handleClick = () => {
    if (onDonate) {
      onDonate();
    }
  };

  return (
    <div className="sidebar-container">
      {!hideHeader && (
        <div className="sidebar-header sticky-header">
          <span className="eyebrow-text color-white">MY CART</span>
          <span className="body-text color-white">
            {`${recipients.length} recipient${recipients.length === 1 ? '' : 's'}`}
          </span>
        </div>
      )}
      <div className="sidebar-body">
        {donatePosition === 'top' && recipients.length > 0 && (
          <DonateButton
            primaryColor={primaryColor}
            secondaryColor={secondaryColor}
            onClick={handleClick}
            wrapperClassName="sidebar-top-button"
          />
        )}
        <span className="body-small-text inkBlue">
          You will be able to set donation amounts at the next step.
        </span>
        {isLoading ? (
          <div className="loader-center" role="status">
            <Loader width={40} height={40} />
            <span className="body-small-text inkBlue">Loading your cart…</span>
          </div>
        ) : recipients.length === 0 ? (
          <span className="body-small-text inkBlue">Your cart is empty</span>
        ) : (
          <>
            <div className="sidebar-items">
              {recipients.map((item, i) => renderItem(item, i, onRemove))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
